/**
 * Storage Benchmark Engine — Storage Benchmark Certification (Phases 2, 4, 6, 8).
 *
 * Rules enforced here:
 *  - A storage benchmark is selected ONLY after the vehicle is classified
 *    (`classifyVehicle`). An unknown class yields no benchmark.
 *  - The benchmark class must match the vehicle class family. Cross-class
 *    comparisons (passenger band against a heavy-duty truck, or vice versa)
 *    are rejected, never silently used.
 *  - Invoice billing unit and benchmark billing unit must match. A per-period
 *    storage charge is normalized to a per-day amount before comparison.
 *  - Storage duration is derived from explicit evidence only (line-item date
 *    range, daily quantity, or extracted storage days) and is flagged when
 *    unknown, partial or estimated. Duration is never silently assumed.
 *
 * Deterministic, pure, and fully unit-tested (`storage-benchmark.test.ts`).
 */

import type { VehicleClass, VehicleClassification } from "./vehicle-classification";
import { VEHICLE_CLASS_LABELS } from "./vehicle-classification";

/* ─────────────────────────── benchmark catalog ─────────────────────────── */

export type StorageBenchmarkClass =
  | "passenger_storage"
  | "motorcycle_storage"
  | "commercial_light_storage"
  | "commercial_medium_storage"
  | "commercial_heavy_duty_storage"
  | "bus_storage"
  | "rv_storage"
  | "trailer_storage"
  | "heavy_equipment_storage"
  | "specialty_storage";

export type StorageRateType =
  | "per_day"
  | "per_calendar_day"
  | "per_24_hours"
  | "per_storage_period";

export type StorageFacilityType =
  | "outdoor"
  | "indoor"
  | "secured"
  | "impound"
  | "commercial_yard"
  | "heavy_equipment_yard";

export interface StorageBenchmark {
  benchmarkClass: StorageBenchmarkClass;
  benchmarkLabel: string;
  /** Vehicle class this band was selected for. */
  vehicleClass: VehicleClass;
  vehicleClassLabel: string;
  facilityType: StorageFacilityType;
  rateType: StorageRateType;
  perDay: { low: number; median: number; high: number };
  provider: string;
  providerConfidence: number;
  datasetVersion: string;
  observedAt: string;
  /** Geography the band covers. */
  locationScope: "zip" | "county" | "state" | "national";
  zip: string | null;
  county: string | null;
  state: string | null;
  freshness: "current" | "acceptable" | "stale" | "unknown";
  ageDays: number | null;
  /** Plain-language reason this benchmark class was selected. */
  selectionReason: string;
}

export const STORAGE_BENCHMARK_LABELS: Record<StorageBenchmarkClass, string> = {
  passenger_storage: "Private Passenger Storage",
  motorcycle_storage: "Motorcycle Storage",
  commercial_light_storage: "Light Commercial Storage",
  commercial_medium_storage: "Medium-Duty Commercial Storage",
  commercial_heavy_duty_storage: "Commercial Heavy-Duty Storage",
  bus_storage: "Bus / Motorcoach Storage",
  rv_storage: "RV Storage",
  trailer_storage: "Trailer Storage",
  heavy_equipment_storage: "Heavy Equipment Storage",
  specialty_storage: "Specialty Vehicle Storage",
};

/** Vehicle class → storage market. This mapping is the certification gate. */
export const VEHICLE_TO_STORAGE_CLASS: Record<VehicleClass, StorageBenchmarkClass | null> = {
  private_passenger: "passenger_storage",
  motorcycle: "motorcycle_storage",
  commercial: "commercial_light_storage",
  straight_truck: "commercial_medium_storage",
  heavy_duty_commercial: "commercial_heavy_duty_storage",
  semi_tractor: "commercial_heavy_duty_storage",
  bus: "bus_storage",
  rv: "rv_storage",
  trailer: "trailer_storage",
  construction_equipment: "heavy_equipment_storage",
  agricultural_equipment: "heavy_equipment_storage",
  specialty: "specialty_storage",
  unknown: null,
};

/** Per-day USD bands (outdoor/secured yard baseline), curated dataset. */
const STORAGE_BANDS: Record<StorageBenchmarkClass, { low: number; median: number; high: number }> = {
  passenger_storage: { low: 25, median: 45, high: 75 },
  motorcycle_storage: { low: 15, median: 28, high: 45 },
  commercial_light_storage: { low: 40, median: 65, high: 110 },
  commercial_medium_storage: { low: 60, median: 95, high: 160 },
  commercial_heavy_duty_storage: { low: 95, median: 150, high: 275 },
  bus_storage: { low: 75, median: 125, high: 225 },
  rv_storage: { low: 45, median: 78, high: 140 },
  trailer_storage: { low: 40, median: 68, high: 120 },
  heavy_equipment_storage: { low: 85, median: 140, high: 250 },
  specialty_storage: { low: 60, median: 100, high: 190 },
};

/** Facility multipliers applied to the outdoor baseline band. */
const FACILITY_MULTIPLIER: Record<StorageFacilityType, number> = {
  outdoor: 1,
  commercial_yard: 1,
  secured: 1.15,
  impound: 1.1,
  indoor: 1.35,
  heavy_equipment_yard: 1.1,
};

export const STORAGE_DATASET_VERSION = "claimguard-storage-2025Q4";
export const STORAGE_PROVIDER = "ClaimGuard Curated Storage Benchmark Dataset";
const STORAGE_OBSERVED_AT = "2025-10-01T00:00:00.000Z";
/** Provider confidence below this value fails certification. */
export const MIN_PROVIDER_CONFIDENCE = 0.6;
/** Vehicle-classification confidence below this value fails certification. */
export const MIN_CLASSIFICATION_CONFIDENCE = 0.5;

function freshnessFor(observedAt: string, now: Date): { freshness: StorageBenchmark["freshness"]; ageDays: number } {
  const age = Math.max(0, Math.round((now.getTime() - Date.parse(observedAt)) / 86_400_000));
  if (age <= 90) return { freshness: "current", ageDays: age };
  if (age <= 365) return { freshness: "acceptable", ageDays: age };
  return { freshness: "stale", ageDays: age };
}

export function detectFacilityType(text: string | null | undefined): StorageFacilityType {
  const t = (text ?? "").toLowerCase();
  if (/\bindoor|inside\s*storage|enclosed\b/.test(t)) return "indoor";
  if (/\bimpound|pound\s*storage\b/.test(t)) return "impound";
  if (/\bsecure[d]?\s*(lot|yard|storage)\b/.test(t)) return "secured";
  if (/\bheavy\s*equipment\s*(yard|storage)\b/.test(t)) return "heavy_equipment_yard";
  if (/\b(yard|lot|outdoor|outside)\b/.test(t)) return "commercial_yard";
  return "outdoor";
}

export interface StorageBenchmarkOptions {
  facilityType?: StorageFacilityType;
  zip?: string | null;
  county?: string | null;
  state?: string | null;
  now?: Date;
}

/**
 * Select the storage benchmark for a classified vehicle. Returns `null` when
 * the vehicle class is unknown — an unclassified vehicle must never be
 * benchmarked against an assumed market.
 */
export function selectStorageBenchmark(
  classification: VehicleClassification,
  opts: StorageBenchmarkOptions = {},
): StorageBenchmark | null {
  const benchmarkClass = VEHICLE_TO_STORAGE_CLASS[classification.vehicleClass];
  if (!benchmarkClass) return null;

  const facilityType = opts.facilityType ?? "outdoor";
  const mult = FACILITY_MULTIPLIER[facilityType];
  const base = STORAGE_BANDS[benchmarkClass];
  const now = opts.now ?? new Date();
  const { freshness, ageDays } = freshnessFor(STORAGE_OBSERVED_AT, now);
  const locationScope: StorageBenchmark["locationScope"] = opts.zip
    ? "zip"
    : opts.county
      ? "county"
      : opts.state
        ? "state"
        : "national";

  // Provider confidence is the benchmark's own reliability, tempered by how
  // confidently the vehicle was classified and how local the geography is.
  const geo = locationScope === "zip" ? 1 : locationScope === "county" ? 0.95 : locationScope === "state" ? 0.9 : 0.8;
  const providerConfidence = Math.round(Math.min(0.95, 0.9 * geo * Math.max(0.5, classification.confidence)) * 100) / 100;

  return {
    benchmarkClass,
    benchmarkLabel: STORAGE_BENCHMARK_LABELS[benchmarkClass],
    vehicleClass: classification.vehicleClass,
    vehicleClassLabel: VEHICLE_CLASS_LABELS[classification.vehicleClass],
    facilityType,
    rateType: "per_day",
    perDay: {
      low: round2(base.low * mult),
      median: round2(base.median * mult),
      high: round2(base.high * mult),
    },
    provider: STORAGE_PROVIDER,
    providerConfidence,
    datasetVersion: STORAGE_DATASET_VERSION,
    observedAt: STORAGE_OBSERVED_AT,
    locationScope,
    zip: opts.zip ?? null,
    county: opts.county ?? null,
    state: opts.state ?? null,
    freshness,
    ageDays,
    selectionReason: `Vehicle classified as ${VEHICLE_CLASS_LABELS[classification.vehicleClass]}; storage compared against the ${STORAGE_BENCHMARK_LABELS[benchmarkClass]} market.`,
  };
}

/* ───────────────────── billing unit + duration (Phases 4/5) ───────────────────── */

export type StorageBillingUnit = "per_day" | "per_period" | "unknown";

export type DurationBasis =
  | "line_item_date_range"
  | "daily_quantity"
  | "extracted_storage_days"
  | "unknown";

export interface NormalizedStorageLine {
  description: string;
  billedAmount: number | null;
  billingUnit: StorageBillingUnit;
  /** Normalized comparable amount, always per storage day. */
  perDayAmount: number | null;
  durationDays: number | null;
  durationBasis: DurationBasis;
  startDate: string | null;
  endDate: string | null;
  /** Non-blocking data-quality flags (unknown/partial/estimated duration). */
  flags: string[];
  /** True when the raw billed amount could not be compared per-day. */
  requiresNormalization: boolean;
  normalizationNote: string | null;
}

export function isStorageDescription(description: string | null | undefined): boolean {
  return /\bstorage\b/i.test(description ?? "");
}

const DATE_RANGE_RE =
  /\(?\s*(\d{1,2}\/\d{1,2}\/\d{2,4})(?:\s+\d{1,2}:\d{2}\s*(?:AM|PM))?\s*\)?\s*(?:through|thru|to|-|–)\s*\(?\s*(\d{1,2}\/\d{1,2}\/\d{2,4})(?:\s+\d{1,2}:\d{2}\s*(?:AM|PM))?\s*\)?/i;

function parseUsDate(s: string): number | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(s.trim());
  if (!m) return null;
  const year = Number(m[3]) < 100 ? 2000 + Number(m[3]) : Number(m[3]);
  const t = Date.UTC(year, Number(m[1]) - 1, Number(m[2]));
  return Number.isFinite(t) ? t : null;
}

export interface StorageLineInput {
  description?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  total?: number | null;
  /** Storage days extracted elsewhere on the claim (weakest basis). */
  extractedStorageDays?: number | null;
}

/**
 * Resolve the billing unit and duration for one storage line and express the
 * charge as a per-day amount so it can be compared to a per-day benchmark.
 */
export function normalizeStorageLine(input: StorageLineInput): NormalizedStorageLine {
  const description = (input.description ?? "").trim();
  const qty = typeof input.quantity === "number" && input.quantity > 0 ? input.quantity : null;
  const unit = typeof input.unitPrice === "number" && input.unitPrice > 0 ? input.unitPrice : null;
  const total =
    typeof input.total === "number" && input.total > 0
      ? input.total
      : qty && unit
        ? round2(qty * unit)
        : unit;
  const flags: string[] = [];

  // 1. Explicit date range on the line is the strongest duration evidence.
  let durationDays: number | null = null;
  let durationBasis: DurationBasis = "unknown";
  let startDate: string | null = null;
  let endDate: string | null = null;

  const range = DATE_RANGE_RE.exec(description.replace(/\s+/g, " "));
  if (range) {
    const a = parseUsDate(range[1]);
    const b = parseUsDate(range[2]);
    if (a !== null && b !== null && b >= a) {
      startDate = new Date(a).toISOString().slice(0, 10);
      endDate = new Date(b).toISOString().slice(0, 10);
      // Storage is billed inclusively by calendar day.
      durationDays = Math.max(1, Math.round((b - a) / 86_400_000) + 1);
      durationBasis = "line_item_date_range";
    } else {
      flags.push("Partial or unparseable storage date range on the invoice line.");
    }
  }

  const perDayWording = /(per\s*day|\/\s*day|daily|per\s*24|calendar\s*day)/i.test(description);

  // 2. A quantity of days at a daily rate.
  if (durationDays === null && qty !== null && qty > 1 && unit !== null) {
    durationDays = qty;
    durationBasis = "daily_quantity";
  }

  // 3. Claim-level extracted storage days — only when it is credible.
  if (durationDays === null && typeof input.extractedStorageDays === "number" && input.extractedStorageDays > 0) {
    durationDays = input.extractedStorageDays;
    durationBasis = "extracted_storage_days";
    flags.push("Storage duration taken from claim-level extraction, not from the storage line itself.");
  }

  // Billing unit resolution.
  let billingUnit: StorageBillingUnit;
  if (perDayWording && (qty === null || qty >= 1)) billingUnit = "per_day";
  else if (durationBasis === "daily_quantity") billingUnit = "per_day";
  else if (durationBasis === "line_item_date_range" && (qty === null || qty <= 1)) billingUnit = "per_period";
  else if (qty !== null && qty > 1) billingUnit = "per_day";
  else billingUnit = "unknown";

  let perDayAmount: number | null = null;
  let normalizationNote: string | null = null;
  let requiresNormalization = false;

  if (billingUnit === "per_day") {
    perDayAmount = unit ?? (total !== null && durationDays ? round2(total / durationDays) : total);
  } else if (billingUnit === "per_period") {
    if (total !== null && durationDays && durationDays > 0) {
      perDayAmount = round2(total / durationDays);
      requiresNormalization = true;
      normalizationNote = `Storage billed as a single period charge of ${fmt(total)} covering ${durationDays} day${durationDays === 1 ? "" : "s"}; normalized to ${fmt(perDayAmount)} per day for benchmark comparison.`;
    } else {
      flags.push("Storage billed per period but the covered duration is unknown — no per-day comparison is possible.");
      requiresNormalization = true;
    }
  } else {
    flags.push("Storage billing unit could not be determined from the invoice line.");
  }

  if (durationDays === null) flags.push("Storage duration unknown — not assumed.");

  return {
    description,
    billedAmount: total,
    billingUnit,
    perDayAmount,
    durationDays,
    durationBasis,
    startDate,
    endDate,
    flags,
    requiresNormalization,
    normalizationNote,
  };
}

/* ─────────────────────────── validation (Phase 8) ─────────────────────────── */

export type StorageValidationCode =
  | "missing_benchmark_category"
  | "missing_benchmark_source"
  | "unknown_vehicle_class"
  | "class_mismatch"
  | "billing_unit_mismatch"
  | "duration_mismatch"
  | "low_provider_confidence"
  | "low_classification_confidence"
  | "stale_benchmark";

export interface StorageValidationIssue {
  code: StorageValidationCode;
  severity: "fail" | "warn";
  message: string;
}

export interface StorageValidationResult {
  pass: boolean;
  issues: StorageValidationIssue[];
}

export function validateStorageBenchmark(args: {
  classification: VehicleClassification;
  benchmark: StorageBenchmark | null;
  line: NormalizedStorageLine;
}): StorageValidationResult {
  const { classification, benchmark, line } = args;
  const issues: StorageValidationIssue[] = [];
  const fail = (code: StorageValidationCode, message: string) =>
    issues.push({ code, severity: "fail", message });
  const warn = (code: StorageValidationCode, message: string) =>
    issues.push({ code, severity: "warn", message });

  if (classification.vehicleClass === "unknown")
    fail("unknown_vehicle_class", "Vehicle class could not be determined; no storage benchmark may be applied.");
  else if (classification.confidence < MIN_CLASSIFICATION_CONFIDENCE)
    fail(
      "low_classification_confidence",
      `Vehicle classification confidence ${classification.confidence} is below the ${MIN_CLASSIFICATION_CONFIDENCE} threshold.`,
    );

  if (!benchmark) {
    fail("missing_benchmark_category", "No storage benchmark category was selected for this storage charge.");
    return { pass: false, issues };
  }

  const expected = VEHICLE_TO_STORAGE_CLASS[classification.vehicleClass];
  if (!expected) fail("missing_benchmark_category", "Vehicle class has no approved storage benchmark market.");
  else if (expected !== benchmark.benchmarkClass)
    fail(
      "class_mismatch",
      `${benchmark.benchmarkLabel} was used for a ${VEHICLE_CLASS_LABELS[classification.vehicleClass]}; the approved market is ${STORAGE_BENCHMARK_LABELS[expected]}.`,
    );

  if (!benchmark.provider || !benchmark.datasetVersion)
    fail("missing_benchmark_source", "Storage benchmark is missing its provider or dataset version.");

  if (benchmark.providerConfidence < MIN_PROVIDER_CONFIDENCE)
    fail(
      "low_provider_confidence",
      `Benchmark provider confidence ${benchmark.providerConfidence} is below the ${MIN_PROVIDER_CONFIDENCE} threshold.`,
    );

  if (benchmark.freshness === "stale")
    fail("stale_benchmark", `Storage benchmark is ${benchmark.ageDays} days old and is considered stale.`);

  if (line.billingUnit === "unknown")
    fail("billing_unit_mismatch", "Invoice storage billing unit is unknown; it cannot be compared to a per-day benchmark.");
  else if (line.perDayAmount === null)
    fail(
      "billing_unit_mismatch",
      "Storage charge could not be normalized to the benchmark's per-day unit.",
    );

  if (line.durationDays === null)
    fail("duration_mismatch", "Storage duration is unknown; per-day normalization cannot be verified.");
  else if (line.durationBasis === "extracted_storage_days")
    warn("duration_mismatch", "Storage duration relies on claim-level extraction rather than the storage line.");

  return { pass: !issues.some((i) => i.severity === "fail"), issues };
}

/* ─────────────────────────── comparison result ─────────────────────────── */

export interface StorageComparison {
  line: NormalizedStorageLine;
  benchmark: StorageBenchmark | null;
  validation: StorageValidationResult;
  /** Per-day variance vs. the benchmark median, when comparable. */
  variancePercent: number | null;
  assessment: "below_market" | "within_market" | "above_market" | "unknown";
  /** Benchmark band expressed over the billed storage period. */
  periodBand: { low: number; median: number; high: number } | null;
}

/**
 * Compare one storage line against the class-matched benchmark, normalizing
 * the billing unit first. Returns `assessment: "unknown"` (never a number)
 * when validation fails, so no invalid comparison can reach the report.
 */
export function compareStorageLine(args: {
  classification: VehicleClassification;
  line: StorageLineInput;
  options?: StorageBenchmarkOptions;
}): StorageComparison {
  const line = normalizeStorageLine(args.line);
  const facilityType = args.options?.facilityType ?? detectFacilityType(line.description);
  const benchmark = selectStorageBenchmark(args.classification, { ...args.options, facilityType });
  const validation = validateStorageBenchmark({ classification: args.classification, benchmark, line });

  if (!validation.pass || !benchmark || line.perDayAmount === null) {
    return { line, benchmark, validation, variancePercent: null, assessment: "unknown", periodBand: null };
  }

  const variancePercent = round2(((line.perDayAmount - benchmark.perDay.median) / benchmark.perDay.median) * 100);
  const assessment =
    line.perDayAmount > benchmark.perDay.high
      ? "above_market"
      : line.perDayAmount < benchmark.perDay.low
        ? "below_market"
        : "within_market";

  const days = line.durationDays ?? 1;
  return {
    line,
    benchmark,
    validation,
    variancePercent,
    assessment,
    periodBand: {
      low: round2(benchmark.perDay.low * days),
      median: round2(benchmark.perDay.median * days),
      high: round2(benchmark.perDay.high * days),
    },
  };
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}
function fmt(n: number) {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
